import type { CSSProperties, MouseEventHandler } from "react";

type ButtonVariant = "primary" | "secondary" | "ghost" | "destructive";

interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
  loading?: boolean;
  /** DOM-specific. Defaults to "button". Pass "submit" to use inside a
   *  `<form onSubmit>` for Enter-key support. */
  type?: "button" | "submit" | "reset";
  className?: string;
  style?: CSSProperties;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-[var(--ish-accent)] text-white hover:opacity-90 " +
    "focus-visible:ring-2 focus-visible:ring-[var(--ish-accent)] focus-visible:ring-offset-2",
  secondary:
    "bg-transparent border border-[var(--ish-accent-border)] " +
    "text-[var(--ish-accent)] hover:bg-[var(--ish-accent-light)] " +
    "focus-visible:ring-2 focus-visible:ring-[var(--ish-accent)] focus-visible:ring-offset-2",
  ghost:
    "bg-transparent text-[var(--ish-text-muted)] " +
    "hover:bg-[color:rgb(0_0_0/0.04)] " +
    "focus-visible:ring-2 focus-visible:ring-[var(--ish-text-muted)] focus-visible:ring-offset-2",
  destructive:
    "bg-[var(--color-error)] text-white hover:opacity-90 " +
    "focus-visible:ring-2 focus-visible:ring-[var(--color-error)] focus-visible:ring-offset-2",
};

export default function Button({
  title,
  onPress,
  variant = "primary",
  disabled = false,
  loading = false,
  type = "button",
  className = "",
  style,
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const handleClick: MouseEventHandler<HTMLButtonElement> = () => {
    onPress();
  };

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      aria-label={title}
      className={
        "inline-flex items-center justify-center rounded-lg px-6 py-3 " +
        "text-[15px] font-semibold leading-none outline-none transition-opacity " +
        variantClasses[variant] +
        " " +
        (isDisabled ? "opacity-50 cursor-not-allowed " : "") +
        className
      }
      style={style}
    >
      {loading ? (
        <span
          className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
          aria-hidden
        />
      ) : (
        title
      )}
    </button>
  );
}
